import type { Dispatch, SetStateAction } from "react";
import { useCallback } from "react";
import { setUser } from "@/integrations/datadog";
import type { AuthSession } from "@/lib/backend";
import { insforge } from "@/lib/insforge";
import { getQueryClient } from "@/lib/query-client";
import type {
  AuthErrorReason,
  AuthProfile,
  StaffSessionOrigin,
  StaffSessionResult,
} from "../types";
import { removeStoredSmsSession } from "./sms-session-storage";
import { getSmsTokenExpiryDelay } from "./use-sms-session-expiry";

interface MutableRef<T> {
  current: T;
}

interface CompleteSignInBindings {
  readonly activeProfileIdRef: MutableRef<string | null>;
  readonly authGenerationRef: MutableRef<number>;
  readonly clearAuthState: (reason: AuthErrorReason | null) => void;
  readonly hasAuthStateRef: MutableRef<boolean>;
  readonly originRef: MutableRef<StaffSessionOrigin | null>;
  readonly pendingSignInRef: MutableRef<boolean>;
  readonly setAuthError: Dispatch<SetStateAction<AuthErrorReason | null>>;
  readonly setIsLoading: Dispatch<SetStateAction<boolean>>;
  readonly setProfile: Dispatch<SetStateAction<AuthProfile | null>>;
  readonly setSession: Dispatch<SetStateAction<AuthSession | null>>;
  readonly smsExpiresAtRef: MutableRef<number | null>;
}

function toAuthSession(result: StaffSessionResult): AuthSession {
  return {
    access_token: result.accessToken,
    expires_at: result.expiresAt ?? undefined,
    user: {
      email: result.user.email,
      id: result.user.id,
      phone: result.user.phone,
      role: result.user.role,
    },
  };
}

function hasInvalidSmsExpiry(result: StaffSessionResult): boolean {
  return result.origin === "sms" && getSmsTokenExpiryDelay(result.expiresAt) === null;
}

function rejectInvalidSmsResult(input: CompleteSignInBindings): false {
  if (!input.activeProfileIdRef.current) {
    input.clearAuthState("session_invalid");
    input.setIsLoading(false);
  }
  return false;
}

function clearReplacedIdentityCache(
  activeProfileIdRef: MutableRef<string | null>,
  nextProfileId: string,
): void {
  const activeProfileId = activeProfileIdRef.current;
  if (activeProfileId && activeProfileId !== nextProfileId) {
    getQueryClient().clear();
  }
}

function applyAcceptedSession(input: CompleteSignInBindings, result: StaffSessionResult): void {
  input.activeProfileIdRef.current = result.profile.id;
  input.originRef.current = result.origin;
  input.smsExpiresAtRef.current = result.origin === "sms" ? (result.expiresAt ?? null) : null;
  removeStoredSmsSession();
  insforge.setAccessToken(result.accessToken);
  input.setSession(toAuthSession(result));
  input.setProfile(result.profile);
  input.setAuthError(null);
  input.setIsLoading(false);
  setUser({
    id: result.profile.id,
    role: result.profile.role ?? undefined,
  });
}

export function useCompleteStaffSignIn({
  activeProfileIdRef,
  authGenerationRef,
  clearAuthState,
  hasAuthStateRef,
  originRef,
  pendingSignInRef,
  setAuthError,
  setIsLoading,
  setProfile,
  setSession,
  smsExpiresAtRef,
}: CompleteSignInBindings): (result: StaffSessionResult) => boolean {
  return useCallback(
    (result: StaffSessionResult) => {
      const input = {
        activeProfileIdRef,
        authGenerationRef,
        clearAuthState,
        hasAuthStateRef,
        originRef,
        pendingSignInRef,
        setAuthError,
        setIsLoading,
        setProfile,
        setSession,
        smsExpiresAtRef,
      };
      if (hasInvalidSmsExpiry(result)) {
        return rejectInvalidSmsResult(input);
      }
      authGenerationRef.current += 1;
      // Refresh listeners read these refs synchronously (see #535). Waiting for
      // React's effect leaves a window where focus misclassifies a valid
      // session as absent and bounces the operator back to /login.
      hasAuthStateRef.current = true;
      pendingSignInRef.current = true;
      clearReplacedIdentityCache(activeProfileIdRef, result.profile.id);
      applyAcceptedSession(input, result);
      return true;
    },
    [
      activeProfileIdRef,
      authGenerationRef,
      clearAuthState,
      hasAuthStateRef,
      originRef,
      pendingSignInRef,
      setAuthError,
      setIsLoading,
      setProfile,
      setSession,
      smsExpiresAtRef,
    ],
  );
}
